import { useQueryState } from "nuqs";
import { cloneElement, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Loader, TabHeaders, Tabs } from "@transfr-inc/dashboard-components";

import { MULTI_ORG_PARENT } from "@constants/organization-types";
import { useConfigurations } from "@hooks";
import { ApplicationId } from "@lib/constants";
import { ConfigurationKeys } from "@services/configuration";
import { getSearchParamValue } from "@utils/navigation";
import { tabParser } from "@utils/search-params-parsers";

import { ChildOrganizationsTable } from "./child-orgs.tab";
import { ImpactReportTab } from "./impact-report.tab";
import { OrganizationDetailTab } from "./organization-detail.tab";
import { UsersTabs } from "./users.tabs";

import "./page.tabs.scss";

export const PageTabs = ({
  orgType,
  organization,
  classrooms,
  users,
  childOrgs,
  parentOrg,
  responsiveStyles,
  actionMenuOptions = {},
  bottomBarOptions,
  dataFiltered,
  isLoading,
}) => {
  const location = useLocation();

  // The Report Builder tab is gated by the org-resolved config-service flag
  // (org override wins over the app-tier value). Fails closed: hidden until
  // the config fetch resolves to true.
  const [impactReportEnabled, setImpactReportEnabled] = useState(false);
  const { getConfigurations } = useConfigurations(
    ApplicationId.DASHBOARD,
    organization?.id,
    {
      onSuccess: (configs) =>
        setImpactReportEnabled(
          configs[ConfigurationKeys.IMPACT_REPORT_ENABLED]?.value === true,
        ),
    },
  );

  useEffect(() => {
    organization?.id && getConfigurations();
  }, [organization?.id]);

  const detailTab = OrganizationDetailTab({
    organization,
    parentOrg,
  });

  const childOrgsTab = ChildOrganizationsTable({
    data: childOrgs,
    parentOrg: organization,
    responsiveStyles,
    actionMenuOptions,
    bottomBarOptions,
  });

  const usersTabs = UsersTabs({
    data: users,
    classrooms,
    isMultiOrg: orgType === MULTI_ORG_PARENT.id,
    responsiveStyles,
    actionMenuOptions,
    bottomBarOptions,
    dataFiltered,
  });

  // ImpactReportTab calls no hooks (it only builds the header/content
  // elements), so invoking it conditionally on a runtime flag is safe.
  const impactReportTab = impactReportEnabled
    ? ImpactReportTab({ organization, childOrgs })
    : undefined;

  const firstTab = orgType === MULTI_ORG_PARENT.id ? childOrgsTab : detailTab;

  // Nuqs sets the tab in the query string, but we need to
  // get it from the location object to detect changes
  const [, setTab] = useQueryState("tab", tabParser);
  const tab = getSearchParamValue(location, "tab", firstTab.name);

  return (
    <>
      <Tabs className={"org-detail-tabs"} selectedTabName={tab}>
        <TabHeaders onSelectedTabChange={(tab) => setTab(tab)}>
          {firstTab.header}
          {usersTabs.map(({ header, name }, i) =>
            cloneElement(header, { key: `tab-header-${name}-${i}` }),
          )}
          {impactReportTab?.header}
        </TabHeaders>
        {firstTab.content}
        {usersTabs.map(({ content, name }, i) =>
          cloneElement(content, { key: `tab-content-${name}-${i}` }),
        )}
        {impactReportTab?.content}
      </Tabs>
      {isLoading && <Loader fixed />}
    </>
  );
};
